import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { invoicesAPI } from '../../services/api';

/**
 * PDF-Vorschau einer Rechnung mit Reitern „Rechnung" und – falls angehängt –
 * „Zeitnachweis". Entwürfe werden frisch gerendert (ohne Statuswechsel),
 * finalisierte Rechnungen zeigen das abgelegte PDF.
 */
const InvoicePreviewDialog = ({ invoice, initialTab = 'invoice', onClose }) => {
  const [tab, setTab] = useState(initialTab);
  const [urls, setUrls] = useState({});
  const [error, setError] = useState('');
  const urlsRef = useRef({});
  urlsRef.current = urls;

  useEffect(() => {
    setTab(initialTab);
    setError('');
  }, [invoice, initialTab]);

  // Je Reiter einmal laden; Blob-URLs beim Schließen freigeben
  useEffect(() => {
    if (!invoice || urls[tab]) return;
    const load = tab === 'timesheet'
      ? invoicesAPI.timesheetPDF(invoice.id)
      : invoice.status === 'draft'
        ? invoicesAPI.previewPDF(invoice.id)
        : invoicesAPI.downloadPDF(invoice.id);
    load
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        setUrls((prev) => ({ ...prev, [tab]: url }));
      })
      .catch(() => setError(tab === 'timesheet'
        ? 'Zeitnachweis konnte nicht geladen werden'
        : 'Rechnungs-PDF konnte nicht geladen werden'));
  }, [invoice, tab, urls]);

  useEffect(() => () => Object.values(urlsRef.current).forEach((u) => window.URL.revokeObjectURL(u)), []);

  if (!invoice) return null;

  const handleClose = () => {
    Object.values(urls).forEach((u) => window.URL.revokeObjectURL(u));
    setUrls({});
    onClose();
  };

  return (
    <Dialog open={!!invoice} onClose={handleClose} maxWidth="lg" fullWidth
            PaperProps={{ sx: { borderRadius: 3, height: '90vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
        <Box sx={{ fontWeight: 600, color: '#1e3a5f' }}>{invoice.invoice_number}</Box>
        <Tabs value={tab} onChange={(e, v) => { setError(''); setTab(v); }} sx={{ flex: 1 }}>
          <Tab value="invoice" label="Rechnung" />
          {invoice.has_timesheet && <Tab value="timesheet" label="Zeitnachweis" />}
        </Tabs>
        <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <Box sx={{ flex: 1, bgcolor: '#525659', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : urls[tab] ? (
          <iframe title={tab} src={urls[tab]} style={{ width: '100%', height: '100%', border: 0 }} />
        ) : (
          <CircularProgress sx={{ color: 'white' }} />
        )}
      </Box>
    </Dialog>
  );
};

export default InvoicePreviewDialog;
